import React, { useEffect, useRef, useState } from 'react';

import { BaseButton, ImageCropModal, useIsMobile, useT } from '@repo/common';

import { ReactComponent as ImageIcon } from '@repo/common/assets/icons/32px/icon_32px_image_filled.svg';
import { ReactComponent as CameraIcon } from '@repo/common/assets/icons/16px/icon_16px_camera_outline.svg';
import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/16px/icon_16px_info_outline.svg';
import { ReactComponent as WarningIcon } from '@repo/common/assets/icons/16px/icon_16px_warning_filled.svg';

import CompanyLogoMenu from './CompanyLogoMenu';

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MIN_IMAGE_SIZE = 200;

const loadImageSize = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const imageUrl = URL.createObjectURL(file);

    image.onload = () => {
      resolve({
        width: image.width,
        height: image.height,
        imageUrl,
      });
    };

    image.onerror = reject;
    image.src = imageUrl;
  });

const dataUrlToFile = async (dataUrl, fileName) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
};

function CompanyLogoField({ value, onChange, onRemove, isDisabled = false }) {
  const t = useT();
  const isMobile = useIsMobile();
  const genericErrorMessage = t(
    'Upload failed. Please try again.',
    '업로드에 실패했습니다. 다시 시도해 주세요.',
  );
  const fileInputRef = useRef(null);
  const editButtonRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const displayImage = value.previewUrl || value.persistedUrl || '';

  useEffect(() => {
    return () => {
      if (selectedImage?.startsWith('blob:')) {
        URL.revokeObjectURL(selectedImage);
      }
    };
  }, [selectedImage]);

  const updateValue = (partial) => {
    onChange({
      ...value,
      ...partial,
    });
  };

  const openFileDialog = () => {
    if (isDisabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    // 원인을 뭉뚱그리면 사용자가 무엇을 고쳐야 할지 알 수 없다
    if (!ACCEPTED_TYPES.includes(file.type)) {
      updateValue({
        error: t('Use a JPG or PNG file.', 'JPG 또는 PNG 파일을 사용해 주세요.'),
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      updateValue({
        error: t(
          'This file is larger than 5MB.',
          '파일 크기가 5MB를 초과합니다.',
        ),
      });
      return;
    }

    try {
      const { width, height, imageUrl } = await loadImageSize(file);

      if (width < MIN_IMAGE_SIZE || height < MIN_IMAGE_SIZE) {
        URL.revokeObjectURL(imageUrl);
        updateValue({
          error: t(
            `This image is ${width}x${height}px. Use at least ${MIN_IMAGE_SIZE}x${MIN_IMAGE_SIZE}px.`,
            `이 이미지는 ${width}x${height}px입니다. 최소 ${MIN_IMAGE_SIZE}x${MIN_IMAGE_SIZE}px 이상이어야 합니다.`,
          ),
        });
        return;
      }

      updateValue({ error: '' });
      setSelectedImage(imageUrl);
      setIsCropOpen(true);
    } catch (error) {
      console.error('Failed to validate company logo:', error);
      updateValue({ error: genericErrorMessage });
    }
  };

  const handleRemove = () => {
    if (selectedImage?.startsWith('blob:')) {
      URL.revokeObjectURL(selectedImage);
    }
    setSelectedImage(null);

    // 저장된 S3 키까지 지워야 해서 폼이 준 핸들러를 우선 쓴다
    if (onRemove) {
      onRemove();
      return;
    }
    updateValue({
      persistedUrl: '',
      previewUrl: '',
      pendingFile: null,
      error: '',
    });
  };

  const handleCropCancel = () => {
    if (selectedImage?.startsWith('blob:')) {
      URL.revokeObjectURL(selectedImage);
    }
    setSelectedImage(null);
    setIsCropOpen(false);
  };

  const handleCropConfirm = async (croppedDataUrl) => {
    try {
      const file = await dataUrlToFile(
        croppedDataUrl,
        `company-logo-${Date.now()}.jpg`,
      );

      updateValue({
        previewUrl: croppedDataUrl,
        pendingFile: file,
        error: '',
      });
    } catch (error) {
      console.error('Failed to prepare cropped company logo:', error);
      updateValue({ error: genericErrorMessage });
    } finally {
      handleCropCancel();
    }
  };

  return (
    <>
      <div className='flex flex-col items-start gap-2 w-full tabletH:pb-10'>
        <div className='typo-b12Strong tabletH:typo-b16 text-text-primary'>
          {t('Company Logo', '회사 로고')}
        </div>

        {!displayImage ? (
          <BaseButton
            type='text'
            size={isMobile ? 's' : 'm'}
            htmlType='button'
            hideIcon
            typo={false}
            isStatic
            onClick={openFileDialog}
            isDisabled={isDisabled}
            textWrapperClass='flex h-auto w-full flex-col items-center justify-center gap-2'
            className='group min-w-0 cursor-pointer p-0 size-[160px] tabletH:size-[200px] rounded-lg border-[1.6px] border-dashed border-grayscale-gray02 bg-grayscale-gray00 transition-colors duration-200 hover:bg-grayscale-gray01 hover:border-grayscale-gray03 active:bg-grayscale-gray02 active:border-grayscale-gray03 disabled:cursor-not-allowed disabled:bg-grayscale-gray01 disabled:border-grayscale-gray02'
          >
            <ImageIcon className='w-[60px] h-[60px] text-icon-placeholder' />
            <div className='flex flex-col items-center gap-0.5 w-full'>
              <div className='typo-b14 text-text-secondary'>
                {t('Upload Image', '이미지 업로드')}
              </div>
              <div className='typo-b11 text-text-placeholder'>
                {t('JPG or PNG, up to 5MB', 'JPG 또는 PNG, 최대 5MB')}
              </div>
            </div>
          </BaseButton>
        ) : (
          <div className='flex flex-col gap-2'>
            <div className='relative size-[160px] tabletH:size-[200px] rounded-lg overflow-hidden border border-grayscale-gray02'>
              <img
                src={displayImage}
                alt={t('Company logo preview', '회사 로고 미리보기')}
                className='w-full h-full object-cover'
              />
              <div className='absolute inset-0 bg-black/0 transition-colors duration-200 hover:bg-black/10 active:bg-black/20' />
              <BaseButton
                type='text_basic'
                size={isMobile ? 's' : 'm'}
                htmlType='button'
                typo={false}
                isStatic
                ref={editButtonRef}
                icon={<CameraIcon className='w-4 h-4 text-icon-secondary' />}
                onClick={() => setIsMenuOpen((prev) => !prev)}
                isDisabled={isDisabled}
                textWrapperClass='h-auto flex-none'
                className='absolute bottom-3 right-3 min-w-0 cursor-pointer'
              >
                {t('edit', '수정')}
              </BaseButton>
            </div>
            {!isMobile && (
              <div className='inline-flex items-start gap-1 text-text-placeholder typo-b11'>
                <InfoIcon className='w-4 h-4 mt-[1px] shrink-0 text-icon-placeholder' />
                <span>
                  {t(
                    'Square image, at least 200x200px, JPG or PNG, up to 5MB',
                    '정사각형 이미지, 최소 200x200px, JPG 또는 PNG, 최대 5MB',
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        {value.error && (
          <div className='inline-flex items-center gap-1 text-functional-failed03 typo-b14'>
            <WarningIcon className='w-4 h-4 shrink-0' />
            <span>{value.error}</span>
          </div>
        )}

        <input
          ref={fileInputRef}
          type='file'
          accept='.jpg,.jpeg,.png'
          className='hidden'
          onChange={handleFileChange}
        />
      </div>

      {isMenuOpen && (
        <CompanyLogoMenu
          anchorRect={editButtonRef.current?.getBoundingClientRect()}
          onClose={() => setIsMenuOpen(false)}
          onUpload={openFileDialog}
          onRemove={handleRemove}
        />
      )}

      {isCropOpen && selectedImage && (
        <ImageCropModal
          image={selectedImage}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
          aspect={1}
          title={t('Company Logo', '회사 로고')}
          cropShape='rect'
          showHintBanner={false}
        />
      )}
    </>
  );
}

export default CompanyLogoField;
